import type { PageMetadata } from "@moment/api-collab/api-types";

import { buildFullSlugPath } from "../page-metadata/page-metadata.atom";
import { getPageDisplayTitle } from "../page-metadata/page-title.utils";

/**
 * Unescape page title from [[link]] format.
 * Reverses: \[ -> [ and \] -> ] and \/ -> / and \\ -> \
 */
export function unescapePageLinkTitle(linkText: string): string {
	return linkText.replace(/\\(.)/g, "$1");
}

/**
 * Split a link text on unescaped forward slashes.
 * Used to separate "Parent/Child" format for subpages.
 *
 * Examples:
 * - "Parent/Child" -> ["Parent", "Child"]
 * - "Page with \/ slash" -> ["Page with / slash"]
 * - "A/B/C" -> ["A", "B", "C"]
 */
export function splitOnUnescapedSlash(linkText: string): string[] {
	const parts: string[] = [];
	let current = "";
	let i = 0;

	while (i < linkText.length) {
		if (linkText[i] === "\\" && i + 1 < linkText.length) {
			// Escaped character - include both the backslash and next char
			current += linkText[i] + linkText[i + 1];
			i += 2;
		} else if (linkText[i] === "/") {
			// Unescaped slash - split here
			parts.push(current);
			current = "";
			i++;
		} else {
			current += linkText[i];
			i++;
		}
	}

	parts.push(current);
	// Filter out empty parts to handle edge cases like "/Child" or "Parent/"
	return parts.filter((part) => part !== "");
}

/**
 * Parse a link text into its components.
 * Handles both simple titles (root pages) and slash-delimited ancestor chains.
 *
 * Returns the unescaped title, its ancestor chain, and optional immediate parent title.
 */
export function parseLinkText(linkText: string): {
	ancestorTitles: string[];
	parentTitle?: string;
	title: string;
} {
	const parts = splitOnUnescapedSlash(linkText);

	if (parts.length === 0) {
		return { ancestorTitles: [], title: "" };
	}

	const unescapedParts = parts.map(unescapePageLinkTitle);
	const title = unescapedParts[unescapedParts.length - 1] ?? "";
	const ancestorTitles = unescapedParts.slice(0, -1);
	const parentTitle = ancestorTitles[ancestorTitles.length - 1];

	return { ancestorTitles, parentTitle, title };
}

/**
 * Escape slashes and backslashes in a title for use in link text.
 * This allows titles containing "/" to be distinguished from parent/child hierarchy.
 */
function escapeSlashInTitle(title: string): string {
	return title.replace(/\\/g, "\\\\").replace(/\//g, "\\/");
}

/**
 * Build the link text for a page (for storing in pageTitle attribute).
 *
 * Slashes in titles are escaped so that parseLinkText can distinguish between
 * "Parent/Child" hierarchy and "Title with \/ in it".
 *
 * Rules:
 * - Root pages (no parent): title with slashes escaped
 * - Nested pages: full ancestor chain joined with "/" and each segment escaped
 */
export function buildPageLinkText(pageId: string, pages: PageMetadata[]): string {
	const page = pages.find((p) => p.id === pageId);
	if (!page) {
		return "";
	}

	const titles: string[] = [];
	const visitedPageIds = new Set<string>();
	let currentPage: PageMetadata | undefined = page;

	while (currentPage && !visitedPageIds.has(currentPage.id)) {
		visitedPageIds.add(currentPage.id);
		titles.unshift(escapeSlashInTitle(getPageDisplayTitle(currentPage.title)));
		currentPage = currentPage.parentPageID
			? pages.find((candidate) => candidate.id === currentPage?.parentPageID)
			: undefined;
	}

	return titles.join("/");
}

function getAncestorTitles(page: PageMetadata, pages: PageMetadata[]): string[] {
	const ancestors: string[] = [];
	const visitedPageIds = new Set<string>();
	let currentPageId = page.parentPageID;

	while (currentPageId && !visitedPageIds.has(currentPageId)) {
		visitedPageIds.add(currentPageId);
		const parent = pages.find((candidate) => candidate.id === currentPageId);
		if (!parent) {
			break;
		}

		ancestors.unshift(parent.title);
		currentPageId = parent.parentPageID;
	}

	return ancestors;
}

function getAncestorPageIds(page: PageMetadata, pages: PageMetadata[]): string[] {
	const ancestors: string[] = [];
	const visitedPageIds = new Set<string>();
	let currentPageId = page.parentPageID;

	while (currentPageId && !visitedPageIds.has(currentPageId)) {
		visitedPageIds.add(currentPageId);
		const parent = pages.find((candidate) => candidate.id === currentPageId);
		if (!parent) {
			break;
		}

		ancestors.unshift(parent.id);
		currentPageId = parent.parentPageID;
	}

	return ancestors;
}

function getSharedPrefixLength(a: string[], b: string[]): number {
	let sharedLength = 0;

	while (sharedLength < a.length && sharedLength < b.length) {
		if (a[sharedLength] !== b[sharedLength]) {
			break;
		}
		sharedLength += 1;
	}

	return sharedLength;
}

function getMatchingPagesByTitle(title: string, pages: PageMetadata[]): PageMetadata[] {
	return pages.filter((page) => !page.archived && page.title === title);
}

function resolveBareTitleLinkText(
	title: string,
	pages: PageMetadata[],
	currentPageId?: string
): PageMetadata | null {
	const matchingPages = getMatchingPagesByTitle(title, pages);
	if (matchingPages.length === 0) {
		return null;
	}

	const rootMatches = matchingPages.filter((page) => getAncestorTitles(page, pages).length === 0);
	if (rootMatches.length > 0) {
		return rootMatches[0] ?? null;
	}

	if (currentPageId) {
		const currentPage = pages.find((page) => page.id === currentPageId && !page.archived);
		if (currentPage) {
			const siblingMatches = matchingPages.filter(
				(page) => page.parentPageID === currentPage.parentPageID
			);
			if (siblingMatches.length === 1) {
				return siblingMatches[0] ?? null;
			}
			if (siblingMatches.length > 1) {
				return null;
			}

			const currentAncestorIds = currentPage.parentPageID
				? getAncestorPageIds(currentPage, pages)
				: [currentPage.id];
			let bestMatches: PageMetadata[] = [];
			let bestScore = 0;

			for (const page of matchingPages) {
				const score = getSharedPrefixLength(
					currentAncestorIds,
					getAncestorPageIds(page, pages)
				);
				if (score > bestScore) {
					bestMatches = [page];
					bestScore = score;
				} else if (score > 0 && score === bestScore) {
					bestMatches.push(page);
				}
			}

			if (bestScore > 0) {
				return bestMatches.length === 1 ? (bestMatches[0] ?? null) : null;
			}
		}
	}

	return matchingPages.length === 1 ? (matchingPages[0] ?? null) : null;
}

/**
 * Resolve a link text to a page.
 *
 * Handles:
 * - "Title" - looks for a root page with matching title, or a unique non-root page
 * - "A/B/C" - looks for a page with the exact ancestor chain ["A", "B"]
 *
 * Returns null if no match found.
 */
export function resolvePageFromLinkText(
	linkText: string,
	pages: PageMetadata[],
	currentPageId?: string
): PageMetadata | null {
	const { ancestorTitles, title } = parseLinkText(linkText);

	if (ancestorTitles.length === 0) {
		return resolveBareTitleLinkText(title, pages, currentPageId);
	}

	return (
		pages
			.filter((p) => !p.archived)
			.find((page) => {
				if (page.title !== title) {
					return false;
				}

				return (
					JSON.stringify(getAncestorTitles(page, pages)) ===
					JSON.stringify(ancestorTitles)
				);
			}) ?? null
	);
}

export function resolvePageReference(
	pageRef: string,
	pages: PageMetadata[],
	currentPageId?: string
): {
	page: PageMetadata;
	fragment?: string;
} | null {
	const resolvedPage = resolvePageFromLinkText(pageRef, pages, currentPageId);
	if (resolvedPage) {
		return { page: resolvedPage };
	}

	for (const { basePageRef, fragment } of getPageReferenceFragmentCandidates(pageRef)) {
		const resolvedBasePage = resolvePageFromLinkText(basePageRef, pages, currentPageId);
		if (resolvedBasePage) {
			return {
				page: resolvedBasePage,
				fragment,
			};
		}
	}

	return null;
}

export function getPageReferenceFragmentCandidates(pageRef: string): Array<{
	basePageRef: string;
	fragment: string;
}> {
	const candidates: Array<{
		basePageRef: string;
		fragment: string;
	}> = [];

	for (let fragmentSeparatorIndex = pageRef.lastIndexOf("#"); fragmentSeparatorIndex > 0; ) {
		if (fragmentSeparatorIndex < pageRef.length - 1) {
			const basePageRef = pageRef.slice(0, fragmentSeparatorIndex);
			const fragment = pageRef.slice(fragmentSeparatorIndex + 1);

			// Treat "#" as a fragment separator only when it cleanly separates the base ref
			// from the fragment. Surrounding whitespace indicates it is likely part of a title.
			if (
				basePageRef.trimEnd() === basePageRef &&
				fragment.trim() === fragment &&
				fragment.length > 0
			) {
				candidates.push({ basePageRef, fragment });
			}
		}

		fragmentSeparatorIndex = pageRef.lastIndexOf("#", fragmentSeparatorIndex - 1);
	}

	return candidates;
}

/**
 * Compare two page metadata states and determine what link text changes are needed.
 *
 * This handles ALL cases where links need updating:
 * - Page renamed: links to that page need title updated
 * - Page moved (parent changed): links need parent prefix added/removed/changed
 * - Parent renamed: child page links need parent prefix updated
 * - Page deleted: orphaned children become root pages
 *
 * Returns a map of oldLinkText -> newLinkText for all changes needed.
 */
export function computeLinkTextUpdates(
	pagesBefore: PageMetadata[],
	pagesAfter: PageMetadata[]
): Map<string, string> {
	const updates = new Map<string, string>();
	const oldLinkCounts = new Map<string, number>();
	const newLinkCounts = new Map<string, number>();

	for (const page of pagesBefore) {
		const linkText = buildPageLinkText(page.id, pagesBefore);
		if (linkText !== "") {
			oldLinkCounts.set(linkText, (oldLinkCounts.get(linkText) ?? 0) + 1);
		}
	}

	for (const page of pagesAfter) {
		const linkText = buildPageLinkText(page.id, pagesAfter);
		if (linkText !== "") {
			newLinkCounts.set(linkText, (newLinkCounts.get(linkText) ?? 0) + 1);
		}
	}

	// Check every page that exists in BOTH states
	// (deleted pages don't need link updates - those links become broken)
	for (const pageAfter of pagesAfter) {
		const pageBefore = pagesBefore.find((p) => p.id === pageAfter.id);
		if (!pageBefore) {
			// New page - no old links to update
			continue;
		}

		const oldLinkText = buildPageLinkText(pageAfter.id, pagesBefore);
		const newLinkText = buildPageLinkText(pageAfter.id, pagesAfter);

		if (
			oldLinkText !== newLinkText &&
			oldLinkText !== "" &&
			(oldLinkCounts.get(oldLinkText) ?? 0) === 1 &&
			(newLinkCounts.get(newLinkText) ?? 0) === 1
		) {
			updates.set(oldLinkText, newLinkText);
		}
	}

	return updates;
}

/**
 * Resolve a page: protocol href to an actual URL path.
 *
 * @param href - The href to resolve (e.g., "page:Parent/Child" or "https://example.com")
 * @param pages - The pages metadata for the current document
 * @param urlPrefix - The URL prefix for internal links (e.g., "/d/workspace/docId")
 * @returns The resolved URL, or the original href if not a page: link or if resolution fails
 *
 * Examples:
 * - "page:My Page" with pages containing "My Page" at slug "my-page" -> "/d/workspace/doc/my-page"
 * - "page:Parent/Child" -> "/d/workspace/doc/parent-slug/child-slug"
 * - "page:NonExistent" -> "/d/workspace/doc/NonExistent" (fallback, will 404)
 * - "https://example.com" -> "https://example.com" (unchanged)
 */
export function resolvePageHref(
	href: string,
	pages: PageMetadata[],
	urlPrefix: string,
	currentPageId?: string
): string {
	// Only process page: protocol links
	if (!href.startsWith("page:")) {
		return href;
	}

	const pageRef = href.slice(5); // Remove "page:" prefix
	const resolvedReference = resolvePageReference(pageRef, pages, currentPageId);

	if (resolvedReference) {
		const fullSlugPath = buildFullSlugPath(pages, resolvedReference.page);
		const pathSuffix = fullSlugPath === "index" ? "" : `/${fullSlugPath}`;
		const fragmentSuffix = resolvedReference.fragment ? `#${resolvedReference.fragment}` : "";
		return `${urlPrefix}${pathSuffix}${fragmentSuffix}`;
	}

	// Page not found - return a fallback URL that will 404.
	// Preserve "/" structure for debugging, but encode "#" so the browser
	// doesn't reinterpret unresolved refs as URL fragments.
	return `${urlPrefix}/${pageRef.replace(/#/g, "%23")}`;
}

/**
 * Transform all page: hrefs in an HTML string to actual URLs.
 *
 * This is useful for post-processing HTML output for export, SSR, or SEO.
 * It finds all href="page:..." attributes and resolves them to actual URLs.
 *
 * @param html - The HTML string to transform
 * @param pages - The pages metadata for the current document
 * @param urlPrefix - The URL prefix for internal links (e.g., "/d/workspace/docId")
 * @returns The HTML with all page: hrefs resolved to actual URLs
 */
export function resolvePageHrefsInHtml(
	html: string,
	pages: PageMetadata[],
	urlPrefix: string,
	currentPageId?: string
): string {
	// Match href="page:..." or href='page:...'
	// Note: This regex does not handle escaped quotes within the href value.
	// Page references containing quote characters will not match correctly.
	return html.replace(/href=(["'])page:([^"']*)\1/g, (_match, quote, pageRef) => {
		const resolvedReference = resolvePageReference(pageRef, pages, currentPageId);

		if (resolvedReference) {
			const fullSlugPath = buildFullSlugPath(pages, resolvedReference.page);
			const pathSuffix = fullSlugPath === "index" ? "" : `/${fullSlugPath}`;
			const fragmentSuffix = resolvedReference.fragment
				? `#${resolvedReference.fragment}`
				: "";
			return `href=${quote}${urlPrefix}${pathSuffix}${fragmentSuffix}${quote}`;
		}

		// Page not found - use fallback while encoding "#" to keep it in the path.
		return `href=${quote}${urlPrefix}/${pageRef.replace(/#/g, "%23")}${quote}`;
	});
}
